
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, ShieldCheck, Percent } from 'lucide-react';

const TermosServiScreen = () => {
    const navigate = useNavigate();

    const SectionTitle = ({ number, title }) => (
        <h2 className="text-sm font-black text-blue-600 uppercase mb-3 tracking-widest">{number}. {title}</h2>
    );

    return (
        <div className="p-6 bg-white h-full overflow-y-auto text-left">
            <div className="flex items-center mb-8 sticky top-0 bg-white py-2 z-10 border-b border-gray-50">
                <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-100 mr-4 transition-colors">
                    <ArrowLeft size={24} className="text-gray-700" />
                </button>
                <h1 className="text-xl font-black text-gray-800 uppercase tracking-tighter">Termos de Uso</h1>
            </div>

            <div className="space-y-8 pb-20">
                <div className="flex items-center space-x-3 text-emerald-600 bg-emerald-50 p-4 rounded-2xl border border-emerald-100">
                    <FileText size={32} />
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-widest leading-none">Plataforma T-SERVI</p>
                        <p className="text-xs font-bold mt-1">Última atualização: fevereiro/2026</p>
                    </div>
                </div>

                <section>
                    <SectionTitle number="1" title="Aceitação dos Termos" />
                    <p className="text-sm text-gray-600 leading-relaxed font-medium">
                        Ao acessar ou utilizar a plataforma T-SERVI, o usuário declara que leu, compreendeu e concorda integralmente com estes Termos de Uso. Caso não concorde, não deverá utilizar a plataforma.
                    </p>
                </section>

                <section>
                    <SectionTitle number="2" title="Identificação da Plataforma" />
                    <p className="text-sm text-gray-600 leading-relaxed font-medium">
                        A T-SERVI é uma plataforma digital de intermediação, identificada para fins de registro de marca como <span className="text-gray-900 font-bold">T-SERVI Autônomos e Especializados</span>.
                    </p>
                </section>

                <section>
                    <SectionTitle number="3" title="Sobre a Plataforma" />
                    <p className="text-sm text-gray-600 leading-relaxed font-medium">
                        A finalidade é conectar clientes a prestadores de serviços autônomos (SERVIs), por meio de ferramentas de busca, comunicação e gestão de orçamentos.
                    </p>
                    <div className="mt-4 bg-amber-50 border border-amber-100 p-4 rounded-2xl flex items-start space-x-3">
                        <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={20} />
                        <p className="text-[11px] text-amber-800 font-bold leading-tight uppercase tracking-tighter">
                            A T-SERVI é uma intermediadora. Não executamos os serviços e não nos responsabilizamos pela qualidade ou resultado dos mesmos.
                        </p>
                    </div>
                </section>

                <section>
                    <SectionTitle number="4" title="Fase Beta" />
                    <p className="text-sm text-gray-500 font-bold mb-3 italic">Durante esta fase, o sistema pode apresentar:</p>
                    <ul className="grid grid-cols-1 gap-2">
                        {['Instabilidades técnicas', 'Falhas ou indisponibilidades', 'Alterações de funções sem aviso prévio'].map((item, i) => (
                            <li key={i} className="text-xs text-gray-600 flex items-center"><span className="w-1.5 h-1.5 bg-amber-400 rounded-full mr-2"></span> {item}</li>
                        ))}
                    </ul>
                </section>

                <section>
                    <SectionTitle number="5" title="Cadastro e Responsabilidades" />
                    <div className="space-y-4">
                        <div>
                            <h3 className="font-bold text-gray-800 mb-2">5.1 Cliente</h3>
                            <ul className="list-disc list-inside space-y-2 text-sm text-gray-600 font-medium leading-relaxed">
                                <li>Utiliza a plataforma gratuitamente para busca.</li>
                                <li>É responsável pela veracidade das informações fornecidas.</li>
                                <li>Decide aceitar ou recusar orçamentos.</li>
                                <li>Responde pelos acordos firmados diretamente com o SERVI.</li>
                            </ul>
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 mb-2">5.2 SERVI (Prestador)</h3>
                            <ul className="list-disc list-inside space-y-2 text-sm text-gray-600 font-medium leading-relaxed">
                                <li>Atua de forma autônoma e independente, sem vínculo empregatício com a T-SERVI.</li>
                                <li>Declara possuir capacidade técnica para executar os serviços.</li>
                                <li>É responsável por todas as obrigações legais, fiscais e trabalhistas.</li>
                                <li>Responde integralmente pela execução, prazos e qualidade do serviço.</li>
                            </ul>
                        </div>
                    </div>
                </section>

                <section className="bg-blue-600 p-6 rounded-[2.5rem] text-white shadow-xl shadow-blue-100 relative overflow-hidden">
                    <div className="relative z-10">
                        <div className="flex items-center space-x-2 mb-2"><Percent size={20} className="text-blue-200" /><h2 className="text-sm font-black uppercase tracking-widest">6. Comissão (Exclusivo para SERVI)</h2></div>
                        <p className="text-xs font-medium leading-relaxed opacity-90 mb-4">Durante a fase beta, o SERVI concorda em contribuir com uma comissão de <span className="text-white font-black">10% (dez por cento)</span> sobre o valor final do serviço.</p>
                        <p className="text-[10px] italic bg-white/10 p-3 rounded-xl border border-white/20">Este valor apoia a manutenção e evolução da plataforma.</p>
                    </div>
                </section>

                <section>
                    <SectionTitle number="7" title="Pagamentos" />
                    <p className="text-sm text-gray-600 leading-relaxed font-medium">A T-SERVI <span className="text-gray-900 font-bold">não processa pagamentos</span>. A negociação e transação ocorrem diretamente entre cliente e SERVI, fora da plataforma.</p>
                </section>

                 <section>
                    <SectionTitle number="8" title="Avaliações e Conduta" />
                    <p className="text-sm text-gray-600 leading-relaxed font-medium">
                        A plataforma permite avaliações mútuas. A T-SERVI poderá remover conteúdos ofensivos ou fraudulentos e suspender/excluir usuários por má-fé.
                    </p>
                </section>

                <section>
                    <SectionTitle number="9" title="Suspensão e Encerramento" />
                    <p className="text-sm text-gray-600 leading-relaxed font-medium">
A T-SERVI poderá suspender ou encerrar contas que violem estes Termos, utilizem a plataforma para fins ilícitos ou causem prejuízo a terceiros.
                    </p>
                </section>

                <section>
                    <SectionTitle number="10" title="Limitação de Responsabilidade" />
                    <p className="text-sm text-gray-600 leading-relaxed font-medium">
A T-SERVI não se responsabiliza por danos, conflitos, inadimplemento ou falhas na execução dos serviços, que são de responsabilidade exclusiva das partes contratantes.
                    </p>
                </section>

                <section>
                    <SectionTitle number="11" title="Propriedade Intelectual" />
                    <p className="text-sm text-gray-600 leading-relaxed font-medium">
                        O nome T-SERVI, sua identidade visual, logotipo e estrutura da plataforma são protegidos, sendo vedado seu uso sem autorização.
                    </p>
                </section>

                <section className="bg-gray-50 p-6 rounded-[2.5rem] border border-gray-100">
                    <h2 className="text-sm font-black text-gray-800 uppercase mb-2 tracking-widest">12 & 13. Alterações e Foro</h2>
                    <p className="text-[11px] text-gray-500 font-bold leading-relaxed uppercase">
                        Os Termos poderão ser alterados a qualquer momento. Fica eleito o foro da República Federativa do Brasil para dirimir controvérsias.
                    </p>
                </section>
            </div>
        </div>
    );
};

export default TermosServiScreen;
